import { Box, useToast } from '@chakra-ui/react';
import { PageLoader, showToast, Text } from '@diamond/shared/ui';
import {
  buyerCaptureException,
  LIST_FEATURE_FLAGS,
  trackEvent,
  useFeatureFlagWithState,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import {
  applyPaymentInstructionSentryContext,
  buildIntentionalPoDetailPaymentProofRoute,
  buildIntentionalPoDetailRoute,
  formatPaymentSubStateLabel,
  resolvePaymentDestination,
  resolvePaymentInstructionPagePhase,
} from '@diamond/sol-buyer/utils';
import {
  useAuthStore,
  useFeatureAccess,
  useGetOrderDetail,
} from '@diamond/sol-buyer-context';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { PaymentInstructionProofPanel } from './PaymentInstructionProofPanel';
import { PaymentInstructionScreen } from './PaymentInstructionScreen';

const getRouteValue = (value: string | string[] | undefined) =>
  typeof value === 'string' ? value : '';

export const PaymentInstructionPage = () => {
  const router = useRouter();
  const reportedInvalidRouteRef = useRef(false);
  const reportedDetailErrorRef = useRef(false);
  const reportedFlagErrorRef = useRef(false);
  const trackedDestinationRef = useRef<string | null>(null);
  const [isUploadFormOpen, setIsUploadFormOpen] = useState(false);
  const [isOcrPickerArmed, setIsOcrPickerArmed] = useState(false);
  const [isLeavingAfterSubmit, setIsLeavingAfterSubmit] = useState(false);
  const [ocrOpenRequestKey, setOcrOpenRequestKey] = useState(0);
  const toast = useToast();
  const accessToken = useAuthStore((state) => state.access_token);
  const paymentInstructionFlag = useFeatureFlagWithState(
    LIST_FEATURE_FLAGS.FF_PAYMENT_INSTRUCTION_PAGE,
    false
  );
  const ocrAccess = useFeatureAccess('payment_proof_ocr');
  const orderMetaId = getRouteValue(router.query.orderMetaId);
  const warehouse = getRouteValue(router.query.warehouse);

  const {
    data: order,
    error,
    isFetching,
    isPending,
    refetch,
  } = useGetOrderDetail(
    orderMetaId,
    warehouse,
    router.isReady &&
      !paymentInstructionFlag.loading &&
      !!orderMetaId &&
      !!warehouse
  );

  const phase = resolvePaymentInstructionPagePhase({
    hasError: Boolean(error),
    flagEnabled: paymentInstructionFlag.value,
    flagLoading: paymentInstructionFlag.loading,
    isFetching,
    isPending,
    order,
    routerReady: router.isReady,
  });

  const replaceWithOrderDetailAfterSubmit = useCallback(() => {
    if (!orderMetaId || !warehouse) return;
    void router.replace(
      buildIntentionalPoDetailPaymentProofRoute({ orderMetaId, warehouse })
    );
  }, [orderMetaId, router, warehouse]);

  const telemetryContext = {
    ffPaymentInstructionPage: paymentInstructionFlag.value,
    orderMetaId,
    paymentMethod: order?.payment_method,
    source: 'instruction' as const,
    warehouse,
  };

  const isOcrEnabled = ocrAccess.isReady && ocrAccess.canUse;
  const showProofPanel = isUploadFormOpen && ocrAccess.isReady;
  const isUploadDisabled = !ocrAccess.isReady;

  const readyOrder = phase.status === 'ready' ? phase.order : undefined;
  const readyDestination = readyOrder
    ? resolvePaymentDestination({
        isPaymentInstructionEnabled: paymentInstructionFlag.value,
        orderMetaId,
        paymentSubState: readyOrder.payment_sub_state,
        warehouse,
      })
    : undefined;

  useEffect(() => {
    if (!router.isReady || paymentInstructionFlag.loading) return;

    if (!orderMetaId || !warehouse) {
      if (!reportedInvalidRouteRef.current) {
        reportedInvalidRouteRef.current = true;
        buyerCaptureException(
          {
            message: 'Payment Instruction route identifiers are missing',
            order_meta_id: orderMetaId,
            warehouse,
          },
          (scope) => {
            applyPaymentInstructionSentryContext(scope, {
              orderMetaId,
              reason: 'invalid-route',
              source: 'instruction',
              warehouse,
            });
          }
        );
      }
      const destination = resolvePaymentDestination({
        isPaymentInstructionEnabled: paymentInstructionFlag.value,
        orderMetaId,
        paymentSubState: null,
        warehouse,
      });
      void router.replace(destination.href);
      return;
    }

    if (!readyOrder || !readyDestination) {
      return;
    }

    const paymentSubState = readyOrder.payment_sub_state;
    const paymentSubStateLabel = formatPaymentSubStateLabel(paymentSubState);
    const destination = readyDestination;
    const destinationTrackingKey = `${paymentSubStateLabel}:${destination.kind}`;

    if (
      paymentInstructionFlag.value &&
      trackedDestinationRef.current !== destinationTrackingKey
    ) {
      trackedDestinationRef.current = destinationTrackingKey;
      trackEvent(TRACKING_EVENTS.WFP_ROUTE_RESOLVED, {
        entry_source: 'payment-instruction',
        ff_payment_instruction_page: paymentInstructionFlag.value,
        order_meta_id: orderMetaId,
        payment_sub_state: paymentSubStateLabel,
        platform: 'web',
        presentation: 'page',
        resolved_destination: destination.kind,
        source: 'instruction',
        sub_state: paymentSubStateLabel,
        warehouse,
      });

      if (destination.kind === 'payment_instruction') {
        trackEvent(TRACKING_EVENTS.VIEW_PAYMENT_INSTRUCTION, {
          entry_source: 'payment-instruction',
          ff_payment_instruction_page: paymentInstructionFlag.value,
          order_meta_id: orderMetaId,
          payment_method: readyOrder.payment_method,
          platform: 'web',
          resolved_destination: destination.kind,
          warehouse,
        });
      } else {
        trackEvent(TRACKING_EVENTS.PAYMENT_INSTRUCTION_SKIPPED, {
          entry_source: 'payment-instruction',
          ff_payment_instruction_page: paymentInstructionFlag.value,
          order_meta_id: orderMetaId,
          payment_sub_state: paymentSubStateLabel,
          payment_method: readyOrder.payment_method,
          platform: 'web',
          resolved_destination: destination.kind,
          warehouse,
        });
      }
    }

    if (destination.kind !== 'payment_instruction') {
      void router.replace(destination.href);
    }
  }, [
    orderMetaId,
    paymentInstructionFlag.loading,
    paymentInstructionFlag.value,
    readyDestination,
    readyOrder,
    router,
    warehouse,
  ]);

  useEffect(() => {
    if (!error || reportedDetailErrorRef.current) return;

    reportedDetailErrorRef.current = true;
    buyerCaptureException(
      {
        error: error instanceof Error ? error.message : String(error),
        message: 'Payment Instruction order detail failed to load',
      },
      (scope) => {
        applyPaymentInstructionSentryContext(scope, {
          orderMetaId,
          reason: 'instruction-detail-load-failed',
          source: 'instruction',
          warehouse,
        });
      }
    );
  }, [error, orderMetaId, warehouse]);

  useEffect(() => {
    if (!paymentInstructionFlag.error || reportedFlagErrorRef.current) return;

    reportedFlagErrorRef.current = true;
    buyerCaptureException(
      {
        error: String(paymentInstructionFlag.error),
        message: 'Payment Instruction feature flag failed to resolve',
      },
      (scope) => {
        applyPaymentInstructionSentryContext(scope, {
          orderMetaId,
          reason: 'feature-flag-resolution-failed',
          source: 'instruction',
          warehouse,
        });
      }
    );
  }, [orderMetaId, paymentInstructionFlag.error, warehouse]);

  const handleOpenUpload = useCallback(() => {
    if (!orderMetaId || !warehouse || !ocrAccess.isReady) return;

    trackEvent(TRACKING_EVENTS.CLICK_UPLOAD_PAYMENT_PROOF, {
      entry_source: 'payment-instruction',
      ff_payment_instruction_page: paymentInstructionFlag.value,
      order_meta_id: orderMetaId,
      platform: 'web',
      resolved_destination: 'payment_instruction',
      warehouse,
    });

    if (ocrAccess.canUse) {
      setIsOcrPickerArmed(true);
      setOcrOpenRequestKey((key) => key + 1);
      return;
    }

    setIsUploadFormOpen(true);
  }, [
    ocrAccess.canUse,
    ocrAccess.isReady,
    orderMetaId,
    paymentInstructionFlag.value,
    warehouse,
  ]);

  const handleExpired = useCallback(() => {
    trackEvent(TRACKING_EVENTS.PAYMENT_INSTRUCTION_EXPIRED_VIEW, {
      entry_source: 'payment-instruction',
      ff_payment_instruction_page: paymentInstructionFlag.value,
      order_meta_id: orderMetaId,
      payment_method: order?.payment_method,
      platform: 'web',
      resolved_destination: 'payment_instruction',
      warehouse,
    });
    void refetch();
  }, [
    order?.payment_method,
    orderMetaId,
    paymentInstructionFlag.value,
    refetch,
    warehouse,
  ]);

  const handleProofSubmitSuccess = useCallback(() => {
    setIsLeavingAfterSubmit(true);
    showToast(toast, 'success', 'Bukti transfer berhasil dikirim');
    trackEvent(TRACKING_EVENTS.SUBMIT_PAYMENT_PROOF_SUCCESS, {
      entry_source: 'payment-instruction',
      ff_payment_instruction_page: paymentInstructionFlag.value,
      order_meta_id: orderMetaId,
      platform: 'web',
      warehouse,
    });
    replaceWithOrderDetailAfterSubmit();
  }, [
    orderMetaId,
    paymentInstructionFlag.value,
    replaceWithOrderDetailAfterSubmit,
    toast,
    warehouse,
  ]);

  const handleUploadStarted = useCallback(() => {
    setIsUploadFormOpen(true);
  }, []);

  if (phase.status === 'waiting') {
    return <PageLoader />;
  }

  if (phase.status === 'error') {
    return (
      <Box p={8}>
        <Text color="#E11D25" textAlign="center">
          Instruksi pembayaran tidak dapat dimuat. Silakan coba lagi.
        </Text>
      </Box>
    );
  }

  if (!readyDestination || readyDestination.kind !== 'payment_instruction') {
    return <PageLoader />;
  }

  const orderPaymentId = phase.order.payment?.id ?? '';

  return (
    <>
      {isLeavingAfterSubmit ? <PageLoader /> : null}
      <PaymentInstructionScreen
        analyticsContext={{
          ffPaymentInstructionPage: paymentInstructionFlag.value,
          orderMetaId,
          warehouse,
        }}
        isUploadDisabled={isUploadDisabled}
        order={phase.order}
        proofPanel={
          (showProofPanel || isOcrPickerArmed) && orderPaymentId ? (
            <PaymentInstructionProofPanel
              accessToken={accessToken}
              mode={isOcrEnabled ? 'ocr' : 'manual'}
              openUploadRequestKey={ocrOpenRequestKey}
              orderPaymentId={orderPaymentId}
              telemetryContext={telemetryContext}
              onSuccess={handleProofSubmitSuccess}
              onUploadStarted={handleUploadStarted}
            />
          ) : undefined
        }
        showProofPanel={showProofPanel}
        onDetailOrder={() => {
          trackEvent(TRACKING_EVENTS.VIEW_PO_DETAIL_WITH_VIEW_PARAM, {
            entry_source: 'payment-instruction',
            ff_payment_instruction_page: paymentInstructionFlag.value,
            order_meta_id: orderMetaId,
            payment_method: phase.order.payment_method,
            platform: 'web',
            resolved_destination: 'order_detail',
            warehouse,
          });
          void router.replace(
            buildIntentionalPoDetailRoute({ orderMetaId, warehouse })
          );
        }}
        onExpired={handleExpired}
        onUpload={handleOpenUpload}
      />
    </>
  );
};
